import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { navLinks } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          scrolled || open ? "bg-white/95 shadow-lg shadow-navy/5 backdrop-blur-xl" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <img
              src={scrolled || open ? "/logo-dark.png" : "/logo-white.png"}
              alt="Vobis Credit"
              className="h-11 w-auto object-contain transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          <nav className="hidden items-center gap-7 xl:gap-9 lg:flex" aria-label="Navigazione principale">
            {navLinks.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === "/"}
                data-testid={`nav-link-${l.slug}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium tracking-wide transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:rounded-full after:bg-gold after:transition-all after:duration-300 ${
                    isActive
                      ? "after:w-full text-gold-dark"
                      : `after:w-0 hover:after:w-full ${scrolled ? "text-navy/70 hover:text-navy" : "text-white/85 hover:text-white"}`
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contatti"
              data-testid="nav-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-navy shadow-lg shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light hover:shadow-xl hover:shadow-gold/30"
            >
              Richiedi una consulenza
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Apri menu"
            className={`transition-colors duration-300 lg:hidden ${scrolled || open ? "text-navy" : "text-white"}`}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-navy px-8 lg:hidden"
          >
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
            <nav className="flex flex-col" aria-label="Menu mobile">
              {navLinks.map((l, i) => (
                <motion.div
                  key={l.path}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.path}
                    end={l.path === "/"}
                    data-testid={`mobile-nav-link-${l.slug}`}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-display text-4xl transition-colors duration-300 ${
                        isActive ? "italic text-gold" : "text-white hover:text-gold-light"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="mt-10"
            >
              <Link
                to="/contatti"
                data-testid="mobile-nav-cta"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-semibold text-navy"
              >
                Richiedi una consulenza
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
